import {
    Button,
    ButtonVariants,
    Dialog,
    FHWAClasses,
    getApiUrl,
    getFHWAClassesDisplayName,
    getSceneTypeDisplayName,
    getTargetTypeDisplayName,
    getTargetTypesDisplayName,
    getTimeOfDayDisplayName,
    getWeatherTypesDisplayName,
    PhotoMeta,
    PhotoMetaSummary,
    SceneType,
    TargetType,
    TargetTypes,
    TimeOfDay,
    WeatherTypes,
} from "@app/shared";
import * as PhotoMetaApi from "@app/shared/src/api/PhotoMetaControllerClient.gen";
import * as TargetApi from "@app/shared/src/api/TargetControllerClient.gen";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { CheckboxInput, EnumSelect, FlagEnumCheckboxInput, NumericInput } from "../../../shared/inputs";

interface Props {
    show: boolean;
    photo: PhotoMetaSummary | null;
    onClose: () => void;
    onSaved: (updated: PhotoMetaSummary) => void;
    onPrev?: () => void;
    onNext?: () => void;
}

const EditorSection: FC<{
    title: string;
    summary?: string;
    children: ReactNode;
}> = ({ title, summary, children }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border-b border-gray-200 pb-2">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex items-center justify-between w-full text-left py-1 group"
            >
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider group-hover:text-gray-700">
                    {title}
                </span>
                <svg
                    className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${expanded ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {!expanded && summary && (
                <p className="text-xs text-gray-600 truncate pb-0.5">{summary}</p>
            )}
            {expanded && <div className="pt-1 pb-1">{children}</div>}
        </div>
    );
};

function flagSummary(value: number, getDisplayName: (v: number) => string | undefined, enumObj: Record<string, number | string>): string {
    if (value === 0) return "None";
    const names: string[] = [];
    for (const [, val] of Object.entries(enumObj)) {
        if (typeof val === "number" && val > 0 && (value & val) !== 0) {
            names.push(getDisplayName(val) ?? String(val));
        }
    }
    return names.join(", ") || "None";
}

const defaultForm = {
    weather: 0,
    timeOfDay: 0 as TimeOfDay,
    scene: 0 as SceneType,
    targetTypesInView: 0,
    fhwaClassesInView: 0,
    dewarpedImage: false,
    numLanes: 0,
    needsReview: false,
};

export const PhotoDetailModal: FC<Props> = ({ show, photo, onClose, onSaved, onPrev, onNext }) => {
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [hiResLoaded, setHiResLoaded] = useState(false);
    const [targetType, setTargetType] = useState<TargetType | undefined>(undefined);

    const thumbnailUrl = photo ? getApiUrl(`/api/photometa/${photo.id}/thumbnail`) : "";
    const imageUrl = photo ? getApiUrl(`/api/photometa/${photo.id}/image`) : "";

    useEffect(() => {
        if (!photo) return;
        setHiResLoaded(false);
        setForm({
            weather: photo.weather as number,
            timeOfDay: photo.timeOfDay,
            scene: photo.scene,
            targetTypesInView: photo.targetTypesInView as number,
            fhwaClassesInView: photo.fhwaClassesInView as number,
            dewarpedImage: photo.dewarpedImage,
            numLanes: photo.numLanes,
            needsReview: photo.needsReview,
        });
    }, [photo]);

    useEffect(() => {
        setTargetType(undefined);
        if (!photo?.externalTargetId || !photo.deviceId) return;

        let cancelled = false;
        TargetApi.getTargetWithHistoryByExternalId(photo.externalTargetId, photo.deviceId).then((target) => {
            if (!cancelled) setTargetType(target?.type);
        });
        return () => { cancelled = true; };
    }, [photo?.externalTargetId, photo?.deviceId]);

    const handleSave = useCallback(async () => {
        if (!photo) return;
        setSaving(true);
        try {
            const payload = new PhotoMeta();
            Object.assign(payload, photo);
            payload.weather = form.weather;
            payload.timeOfDay = form.timeOfDay;
            payload.scene = form.scene;
            payload.targetTypesInView = form.targetTypesInView;
            payload.fhwaClassesInView = form.fhwaClassesInView;
            payload.dewarpedImage = form.dewarpedImage;
            payload.numLanes = form.numLanes;
            payload.needsReview = form.needsReview;

            const result = await PhotoMetaApi.update(photo.id, payload);
            if (result) onSaved(result);
        } finally {
            setSaving(false);
        }
    }, [form, photo, onSaved]);

    const handleDownload = () => {
        if (!photo) return;
        window.open(getApiUrl(`/api/photometa/${photo.id}/image`), "_blank");
    };

    useEffect(() => {
        if (!show) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft" && onPrev) onPrev();
            if (e.key === "ArrowRight" && onNext) onNext();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [show, onClose, onPrev, onNext]);

    return (
        <Dialog
            show={show}
            title="Photo Details"
            onCancelClick={onClose}
            showOkButton={false}
            showCancelButton={false}
            className="max-w-5xl w-full max-h-[90vh]"
        >
            {photo && <div className="flex overflow-hidden -mx-5 -my-5" style={{ height: "calc(90vh - 80px)" }}>
                <div className="flex-1 bg-gray-900 flex items-center justify-center relative">
                    <img
                        src={thumbnailUrl}
                        alt="Thumbnail preview"
                        className={`absolute inset-0 w-full h-full object-contain ${hiResLoaded ? "opacity-0" : "opacity-100"}`}
                    />
                    <img
                        key={photo.id}
                        src={imageUrl}
                        alt="Full resolution"
                        className={`absolute inset-0 w-full h-full object-contain ${hiResLoaded ? "opacity-100" : "opacity-0"}`}
                        onLoad={() => setHiResLoaded(true)}
                    />
                    {!hiResLoaded && (
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white/80 text-xs px-3 py-1 rounded-full">
                            Loading full resolution…
                        </div>
                    )}
                    {onPrev && (
                        <button onClick={onPrev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 text-lg">
                            &#8249;
                        </button>
                    )}
                    {onNext && (
                        <button onClick={onNext} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 text-lg">
                            &#8250;
                        </button>
                    )}
                </div>

                <div className="w-80 border-l border-gray-200 flex flex-col">
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                        <div className="text-xs text-gray-500 space-y-1 pb-2 border-b border-gray-200">
                            <p className="font-mono text-[10px] text-gray-400 select-all" title="Photo ID">{photo.id}</p>
                            <p><span className="font-medium text-gray-700">Device:</span> {photo.deviceName ?? "Unknown"}</p>
                            <p><span className="font-medium text-gray-700">Captured:</span> {photo.targetCaptureDate?.toLocaleString()}</p>
                            <p><span className="font-medium text-gray-700">Created:</span> {photo.dateCreated?.toLocaleString()}</p>
                            {photo.externalTargetId && <p><span className="font-medium text-gray-700">Target Type:</span> {targetType !== undefined ? (getTargetTypeDisplayName(targetType) ?? "Unknown") : "Loading…"}</p>}
                            {photo.width && photo.height && <p><span className="font-medium text-gray-700">Size:</span> {photo.width} x {photo.height}</p>}
                        </div>

                        <EditorSection
                            title="Time of Day"
                            summary={getTimeOfDayDisplayName(form.timeOfDay) ?? "Unknown"}
                        >
                            <EnumSelect
                                enumType={TimeOfDay}
                                value={form.timeOfDay}
                                onChange={(val) => setForm((f) => ({ ...f, timeOfDay: (val ?? 0) as TimeOfDay }))}
                                nameToDisplayConverter={getTimeOfDayDisplayName}
                                sort
                            />
                        </EditorSection>

                        <EditorSection
                            title="Scene"
                            summary={getSceneTypeDisplayName(form.scene) ?? "Unknown"}
                        >
                            <EnumSelect
                                enumType={SceneType}
                                value={form.scene}
                                onChange={(val) => setForm((f) => ({ ...f, scene: (val ?? 0) as SceneType }))}
                                nameToDisplayConverter={getSceneTypeDisplayName}
                                sort
                            />
                        </EditorSection>

                        <EditorSection
                            title="Weather"
                            summary={flagSummary(form.weather, getWeatherTypesDisplayName, WeatherTypes as any)}
                        >
                            <FlagEnumCheckboxInput
                                enumType={WeatherTypes}
                                value={form.weather}
                                onChange={(v) => setForm((f) => ({ ...f, weather: v ?? 0 }))}
                                keyFilter={(k) => typeof k === "number" && k > 0}
                                sort
                            />
                        </EditorSection>

                        <EditorSection
                            title="Target Types In View"
                            summary={flagSummary(form.targetTypesInView, getTargetTypesDisplayName, TargetTypes as any)}
                        >
                            <FlagEnumCheckboxInput
                                enumType={TargetTypes}
                                value={form.targetTypesInView}
                                onChange={(v) => setForm((f) => ({ ...f, targetTypesInView: v ?? 0 }))}
                                keyFilter={(k) => typeof k === "number" && k > 0}
                                sort
                            />
                        </EditorSection>

                        <EditorSection
                            title="FHWA Classes In View"
                            summary={flagSummary(form.fhwaClassesInView, getFHWAClassesDisplayName, FHWAClasses as any)}
                        >
                            <FlagEnumCheckboxInput
                                enumType={FHWAClasses}
                                value={form.fhwaClassesInView}
                                onChange={(v) => setForm((f) => ({ ...f, fhwaClassesInView: v ?? 0 }))}
                                keyFilter={(k) => typeof k === "number" && k > 0}
                                sort
                            />
                        </EditorSection>

                        <EditorSection
                            title="Flags"
                            summary={[
                                form.needsReview ? "Needs Review" : undefined,
                                form.dewarpedImage ? "Dewarped" : undefined,
                            ].filter(Boolean).join(", ") || "None"}
                        >
                            <div className="flex items-center gap-4">
                                <CheckboxInput
                                    label="Needs Review"
                                    checked={form.needsReview}
                                    onChange={() => setForm((f) => ({ ...f, needsReview: !f.needsReview }))}
                                />
                                <CheckboxInput
                                    label="Dewarped"
                                    checked={form.dewarpedImage}
                                    onChange={() => setForm((f) => ({ ...f, dewarpedImage: !f.dewarpedImage }))}
                                />
                            </div>
                        </EditorSection>

                        <EditorSection
                            title="Num Lanes"
                            summary={String(form.numLanes)}
                        >
                            <NumericInput
                                value={form.numLanes}
                                onChange={(val) => setForm((f) => ({ ...f, numLanes: val ?? 0 }))}
                                min={0}
                                allowFloat={false}
                            />
                        </EditorSection>
                    </div>

                    <div className="flex gap-2 p-4 border-t border-gray-200 flex-shrink-0">
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? "Saving..." : "Save"}
                        </Button>
                        <Button variant={ButtonVariants.Secondary} onClick={handleDownload}>
                            Download
                        </Button>
                    </div>
                </div>
            </div>}
        </Dialog>
    );
};
